import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  Download,
  MessageCircle,
  FileText,
  Ruler,
} from "lucide-react";
import { collections, company } from "../data/site";
import { Reveal } from "../components/Reveal";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = collections.find((c) => c.slug === slug);
  const [activeImg, setActiveImg] = useState(0);

  if (!product) {
    return (
      <div className="pt-40 pb-24 max-w-4xl mx-auto px-6 text-center">
        <p className="text-[#888]">Koleksiyon bulunamadı.</p>
        <button
          onClick={() => navigate("/urunler")}
          className="btn-ghost mt-6"
        >
          Ürünlere Dön
        </button>
      </div>
    );
  }

  const similar = collections
    .filter((c) => c.category === product.category && c.slug !== slug)
    .slice(0, 4);

  return (
    <div data-testid="product-detail" className="pt-32 pb-24 bg-[#FAFAFA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#888] mb-8">
          <Link to="/" className="hover:text-[#B8860B]">
            Ana Sayfa
          </Link>
          <ChevronRight size={12} />
          <Link to="/urunler" className="hover:text-[#B8860B]">
            Ürünler
          </Link>
          <ChevronRight size={12} />
          <Link
            to={`/urunler?kategori=${product.category}`}
            className="hover:text-[#B8860B]"
          >
            {product.category}
          </Link>
          <ChevronRight size={12} />
          <span className="text-[#1E1E1E]">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="clip-frame aspect-[4/3] bg-[#F5F5F5] overflow-hidden">
              <img
                src={product.gallery[activeImg]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {product.gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`aspect-square overflow-hidden border-2 transition-colors ${
                    activeImg === i ? "border-[#B8860B]" : "border-transparent"
                  }`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
              {product.category} · {product.color}
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#1E1E1E] leading-tight">
              {product.name}
            </h1>
            <p className="mt-6 text-[15px] text-[#555] leading-relaxed">
              {product.description}
            </p>

            <div className="mt-10 border-t border-black/8">
              {Object.entries(product.tech).map(([k, v]) => (
                <div
                  key={k}
                  className="flex justify-between py-3 border-b border-black/8 text-[13px]"
                >
                  <span className="text-[#888] tracking-wider uppercase text-[11px]">
                    {k}
                  </span>
                  <span className="text-[#1E1E1E] font-medium">{v}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-2 flex-wrap">
              {product.sizes.map((s) => (
                <span
                  key={s}
                  className="text-[11px] uppercase tracking-wider border border-[#1E1E1E]/15 px-3 py-1.5 text-[#666] flex items-center gap-1.5"
                >
                  <Ruler size={12} /> {s}
                </span>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/teklif-al" data-testid="pd-cta-quote" className="btn-gold">
                Teklif Al <ArrowRight size={14} />
              </Link>
              <a
                href={company.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid="pd-cta-whatsapp"
                className="btn-ghost"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <a
                href="#"
                data-testid="pd-cta-catalog"
                onClick={(e) => e.preventDefault()}
                className="btn-ghost"
              >
                <FileText size={14} /> PDF Katalog
              </a>
            </div>

            <div className="mt-10 p-6 border border-[#B8860B]/25 bg-white">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#B8860B] mb-2">
                Örnek Talep
              </div>
              <p className="text-[13px] text-[#555] leading-relaxed">
                Uygulama karar aşamasındaysanız, 30×30 cm gerçek örnekleri
                ücretsiz olarak size ulaştırıyoruz. WhatsApp üzerinden
                iletebilirsiniz.
              </p>
            </div>
          </div>
        </div>

        {/* Similar */}
        {similar.length > 0 && (
          <div className="mt-24">
            <div className="flex justify-between items-end mb-10">
              <div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
                  Benzer Ürünler
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E]">
                  Aynı ailenin diğer koleksiyonları
                </h2>
              </div>
              <Link
                to={`/urunler?kategori=${product.category}`}
                className="btn-ghost"
              >
                Tümü <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {similar.map((c) => (
                <Link
                  key={c.slug}
                  to={`/urunler/${c.slug}`}
                  className="group block"
                >
                  <div className="clip-frame aspect-[4/5] overflow-hidden">
                    <img
                      src={c.hero}
                      alt={c.name}
                      loading="lazy"
                      className="w-full h-full object-cover img-zoom"
                    />
                  </div>
                  <div className="mt-3">
                    <div className="text-[10px] tracking-wider uppercase text-[#888]">
                      {c.finish}
                    </div>
                    <div className="text-[15px] font-medium text-[#1E1E1E] group-hover:text-[#B8860B] transition-colors">
                      {c.name}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
